import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Checkbox, Col, DatePicker, Dropdown, Empty, Form, Input, Modal, Row, Select, Space, Table, Tag, Tooltip, message } from 'antd';
import CBInputNumber from '../../../components/CBInputNumber';
import {
    PlusOutlined, ReloadOutlined, CheckCircleOutlined,
    CloseCircleOutlined, SearchOutlined,
    DownloadOutlined, FileTextOutlined, FileExcelOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { XLSX } from '../../../utils/xlsxShim';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import axiosInstance from '../../../variables/api/axiosInstance';
import { formatDate, formatMontant, isDossierMurOuPlus, STATUT_MAP } from '../../../variables/constants';
import KpiCards from '../../../components/KpiCards';
import TableExportButton from '../../../components/TableExportButton';
import logoLocal from '../../../assets/images/logo.png';
import { avecLibelleTraduit, libellesTraduits } from '../../../i18n/libelles';

const hexToRgb = (hex = '#1a6b3c') => {
    const c = hex.replace('#', '');
    return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
};

const TYPES_EMETTEUR = avecLibelleTraduit([
    { value: 'BUREAU_NATIONAL' },
    { value: 'BUREAU_HOMOLOGUE' },
    { value: 'COMPAGNIE_MEMBRE' },
], 'dossierFinance:encaissements.typesEmetteur');

const { Option } = Select;

const STATUT_COLORS = {
    RECU: 'blue',
    ENCAISSE: 'green',
    REVENU_SANS_PROVISION: 'orange',
    ANNULE: 'red',
};
const STATUT_LABELS = libellesTraduits('dossierFinance:encaissements.statuts');

export default function OngletEncaissements({ sinistre }) {
    const { t } = useTranslation('dossierFinance');
    const sinistreId = sinistre?.sinistreTrackingId;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [banques, setBanques] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [encaisserModal, setEncaisserModal] = useState({ open: false, item: null });
    const [annulerModal, setAnnulerModal] = useState({ open: false, item: null });

    const [filtreStatut, setFiltreStatut] = useState('');
    const [search, setSearch] = useState('');

    const [prefinsARembourser, setPrefinsARembourser] = useState([]);

    //  État sélecteurs organisme émetteur 
    const [typeEmetteur, setTypeEmetteur] = useState(null);
    const [organismeEmetteurId, setOrganismeEmetteurId] = useState(null);
    //  État chèque à l'ordre de 
    const [chequeOrdreOrganismeId, setChequeOrdreOrganismeId] = useState(null);

    const [form] = Form.useForm();
    const [encaisserForm] = Form.useForm();
    const [annulerForm] = Form.useForm();

    //  Query : organismes filtrés par type émetteur 
    const { data: organismes = [], isFetching: isFetchingOrganismes } = useQuery({
        queryKey: ['organismes', 'by-type', typeEmetteur],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/v1/organismes/by-type', {
                params: { typeOrganisme: [typeEmetteur] },
                paramsSerializer: { indexes: null }
            });
            const list = data?.data ?? [];
            // Filtrer les compagnies togolaises par codePays
            if (typeEmetteur === 'COMPAGNIE_MEMBRE') {
                return list.filter(o => o.codePays === 'TGO');
            }
            return list;
        },
        enabled: !!typeEmetteur,
    });

    //  Query : liste BNCB (chèque à l'ordre de) 
    const { data: bncbList = [] } = useQuery({
        queryKey: ['organismes', 'bureau-national'],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/v1/organismes/by-type', {
                params: { typeOrganisme: ['BUREAU_NATIONAL'] },
                paramsSerializer: { indexes: null }
            });
            return data?.data ?? [];
        },
    });

    // Pré-sélectionner le premier BNCB dès que la liste est chargée
    useEffect(() => {
        if (bncbList.length > 0 && !chequeOrdreOrganismeId) {
            setChequeOrdreOrganismeId(bncbList[0].organismeTrackingId);
        }
    }, [bncbList]); // eslint-disable-line react-hooks/exhaustive-deps

    const queryClient = useQueryClient();

    //  Mutation création encaissement 
    const creerEncaissement = useMutation({
        mutationFn: async (body) => {
            const { data } = await axiosInstance.post('/v1/encaissements', body);
            return data;
        },
        onSuccess: (data, variables) => {
            const sid = variables.sinistreTrackingId;
            queryClient.invalidateQueries({ queryKey: ['encaissement-exists', sid] });
            queryClient.invalidateQueries({ queryKey: ['encaissements', sid] });
            queryClient.invalidateQueries({ queryKey: ['reglement-bloquant-info', sid] });
            toast.success(t('encaissements.messages.enregistre'));
            form.resetFields();
            setTypeEmetteur(null);
            setOrganismeEmetteurId(null);
            setModalOpen(false);
            load();
        },
        onError: (e) => {
            toast.error(e.response?.data?.message || t('encaissements.messages.erreurEnregistrement'));
        },
    });

    const load = async () => {
        if (!sinistreId) return;
        setLoading(true);
        try {
            const [encRes, bRes] = await Promise.all([
                axiosInstance.get(`/v1/encaissements/sinistre/${sinistreId}`)
                    .catch(() => ({ data: { data: [] } })),
                axiosInstance.get('/v1/banques')
                    .catch(() => ({ data: { data: [] } })),
            ]);
            const all = (encRes.data?.data || []).sort((a, b) => {
                const da = a.dateReception || a.dateEmission || '';
                const db = b.dateReception || b.dateEmission || '';
                return db.localeCompare(da);
            });
            setItems(all);
            setBanques(bRes.data?.data || []);
        } catch {
            toast.error(t('encaissements.messages.erreurChargement'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [sinistreId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadPrefinsARembourser = async () => {
        if (!sinistreId) return;
        try {
            const res = await axiosInstance.get(`/v1/prefinancements/sinistre/${sinistreId}`);
            const list = (res.data?.data || []).filter(p =>
                p.statut === 'VALIDE' || p.statut === 'REMBOURSE_PARTIEL');
            setPrefinsARembourser(list);
        } catch { /* silent */ }
    };

    useEffect(() => { loadPrefinsARembourser(); /* eslint-disable-line */ }, [sinistreId, items.length]);

    const filtered = useMemo(() => items.filter(it => {
        if (filtreStatut && it.statutCheque !== filtreStatut) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                (it.numeroCheque || '').toLowerCase().includes(q) ||
                (it.organismeEmetteurRaisonSociale || '').toLowerCase().includes(q) ||
                (it.banqueEmettrice || '').toLowerCase().includes(q)
            );
        }
        return true;
    }), [items, filtreStatut, search]);

    const totaux = useMemo(() => filtered.reduce((acc, it) => {
        if (it.statutCheque !== 'ANNULE') {
            acc.montant += Number(it.montantCheque || 0);
            acc.frais += Number(it.produitFraisGestion || 0);
        }
        return acc;
    }, { montant: 0, frais: 0 }), [filtered]);

    const enAttente = useMemo(
        () => items.filter(i => i.statutCheque === 'RECU').length,
        [items]
    );

    const organismeEmetteur = useMemo(
        () => filtered[0]?.organismeEmetteurRaisonSociale || null,
        [filtered]
    );

    //  Soumission formulaire création 
    const creer = (vals) => {
        creerEncaissement.mutate({
            numeroCheque: vals.numeroCheque,
            montantCheque: vals.montantCheque,
            montantTheorique: vals.montantTheorique,
            appliquerFraisGestion: vals.appliquerFraisGestion,
            dateEmission: vals.dateEmission?.format('YYYY-MM-DD'),
            dateReception: vals.dateReception?.format('YYYY-MM-DD'),
            banqueEmettrice: vals.banqueEmettrice,
            // Organisme émetteur : issu du sélecteur 2 étapes
            organismeEmetteurTrackingId: organismeEmetteurId,
            // Chèque à l'ordre de : BNCB pré-sélectionné (ou choix utilisateur)
            chequeOrdreOrganismeTrackingId: chequeOrdreOrganismeId,
            sinistreTrackingId: sinistreId,
        });
    };

    const encaisser = async (vals) => {
        try {
            await axiosInstance.patch(
                `/v1/encaissements/${encaisserModal.item.encaissementTrackingId}/encaisser`,
                null,
                { params: { dateEncaissement: vals.dateEncaissement.format('YYYY-MM-DD') } }
            );
            toast.success(t('encaissements.messages.chequeEncaisse'));
            encaisserForm.resetFields();
            setEncaisserModal({ open: false, item: null });
            load();
        } catch (e) {
            toast.error(e.response?.data?.message || t('encaissements.messages.erreur'));
        }
    };

    const annuler = async (vals) => {
        try {
            await axiosInstance.patch(
                `/v1/encaissements/${annulerModal.item.encaissementTrackingId}/annuler`,
                null,
                { params: { motif: vals.motif } }
            );
            toast.success(t('encaissements.messages.annule'));
            annulerForm.resetFields();
            setAnnulerModal({ open: false, item: null });
            load();
        } catch (e) {
            toast.error(e.response?.data?.message || t('encaissements.messages.erreur'));
        }
    };

    const moneyProps = { style: { width: '100%' }, min: 0, step: 1000 };

    //  Export helpers 
    const buildExportRows = () => filtered.map(it => ({
        'N° Chèque': it.numeroCheque || '',
        'Organisme émetteur': it.organismeEmetteurRaisonSociale || '',
        'Banque': it.banqueEmettrice || '',
        'Montant chèque (FCFA)': it.montantCheque != null ? Number(it.montantCheque) : '',
        'Montant théorique (FCFA)': it.montantTheorique != null ? Number(it.montantTheorique) : '',
        'Frais gestion (FCFA)': it.produitFraisGestion != null ? Number(it.produitFraisGestion) : '',
        'Date émission': it.dateEmission ? new Date(it.dateEmission).toLocaleDateString('fr-FR') : '',
        'Date réception': it.dateReception ? new Date(it.dateReception).toLocaleDateString('fr-FR') : '',
        'Date encaissement': it.dateEncaissement ? new Date(it.dateEncaissement).toLocaleDateString('fr-FR') : '',
        'Statut': STATUT_LABELS[it.statutCheque] || it.statutCheque || '',
        'Motif annulation': it.motifAnnulation || '',
    }));

    const exportFileBaseName = `encaissements_sinistre_${sinistre?.numeroSinistreLocal || sinistreId?.slice(0, 8) || 'export'}`;

    const exportToPdf = () => {
        const rows = buildExportRows();
        if (!rows.length) { message.info('Aucune donnée à exporter'); return; }
        try {
            const doc = new jsPDF({ orientation: 'landscape' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const rgb = hexToRgb('#1a6b3c');
            let cursorY = 10;

            doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
            doc.setLineWidth(1);
            doc.line(14, cursorY, pageWidth - 14, cursorY);
            cursorY += 8;

            doc.setFontSize(14);
            doc.setTextColor(rgb[0], rgb[1], rgb[2]);
            doc.text(`Encaissements - Sinistre N° ${sinistre?.numeroSinistreLocal || ''}`, pageWidth / 2, cursorY, { align: 'center' });
            cursorY += 6;

            if (sinistre?.organismeHomologueRaisonSociale) {
                doc.setFontSize(10);
                doc.setTextColor(80, 80, 80);
                doc.text(`Bureau homologue : ${sinistre.organismeHomologueRaisonSociale}`, pageWidth / 2, cursorY, { align: 'center' });
                cursorY += 5;
            }

            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, cursorY, { align: 'center' });
            cursorY += 6;

            const headers = Object.keys(rows[0]);
            autoTable(doc, {
                head: [headers],
                body: rows.map(r => headers.map(h => r[h])),
                startY: cursorY,
                styles: { fontSize: 8, cellPadding: 3 },
                headStyles: { fillColor: rgb, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
                alternateRowStyles: { fillColor: [245, 247, 250] },
                columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
            });

            doc.save(`${exportFileBaseName}.pdf`);
        } catch (e) {
            console.error(e);
            message.error("Erreur lors de l'export PDF");
        }
    };

    const exportToExcel = async () => {
        const rows = buildExportRows();
        if (!rows.length) { message.info('Aucune donnée à exporter'); return; }
        try {
            const titre = `Encaissements - Sinistre N° ${sinistre?.numeroSinistreLocal || ''} · Bureau homologue : ${sinistre?.organismeHomologueRaisonSociale || ''} - Exporté le ${new Date().toLocaleDateString('fr-FR')}`;
            const headers = Object.keys(rows[0]);
            const wsData = [
                [titre],
                [],
                headers,
                ...rows.map(r => headers.map(h => r[h])),
            ];
            const worksheet = XLSX.utils.aoa_to_sheet(wsData);
            worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
            worksheet['!cols'] = headers.map(h => ({
                wch: h === 'Organisme émetteur' ? 26 : h === 'N° Chèque' ? 18 : h === 'Motif annulation' ? 30 : 16,
            }));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Encaissements');
            await XLSX.writeFile(workbook, `${exportFileBaseName}.xlsx`);
        } catch (e) {
            console.error(e);
            message.error("Erreur lors de l'export Excel");
        }
    };

    const exportToCsv = () => {
        const rows = buildExportRows();
        if (!rows.length) { message.info('Aucune donnée à exporter'); return; }
        try {
            const headers = Object.keys(rows[0]);
            const escape = v => { const s = v == null ? '' : String(v); return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
            const titre = `Encaissements - Sinistre N° ${sinistre?.numeroSinistreLocal || ''} · Bureau homologue : ${sinistre?.organismeHomologueRaisonSociale || ''} - Exporté le ${new Date().toLocaleDateString('fr-FR')}`;
            const csv = [titre, '', headers.join(';'), ...rows.map(r => headers.map(h => escape(r[h])).join(';'))].join('\n');
            const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${exportFileBaseName}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            message.error("Erreur lors de l'export CSV");
        }
    };

    const cols = [
        {
            title: t('encaissements.colonnes.numeroCheque'), dataIndex: 'numeroCheque', key: 'num', width: 160,
            render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}>{v}</span>
        },
        {
            title: t('encaissements.colonnes.montant'), dataIndex: 'montantCheque', key: 'mt', width: 140, align: 'right',
            render: v => <span style={{ fontWeight: 600 }}>{formatMontant(v)}</span>
        },
        {
            title: t('encaissements.colonnes.theorique'), dataIndex: 'montantTheorique', key: 'th', width: 130, align: 'right',
            render: v => formatMontant(v)
        },
        {
            title: t('encaissements.colonnes.fraisGestion'), dataIndex: 'produitFraisGestion', key: 'fg', width: 120, align: 'right',
            render: v => v ? <span style={{ color: '#1a6b3c', fontWeight: 600 }}>{formatMontant(v)}</span> : '-'
        },
        { title: t('encaissements.colonnes.banque'), dataIndex: 'banqueEmettrice', key: 'bq', width: 150, ellipsis: true },
        { title: t('encaissements.colonnes.reception'), dataIndex: 'dateReception', key: 'dr', width: 110, render: v => formatDate(v) },
        {
            title: t('encaissements.colonnes.encaissement'), dataIndex: 'dateEncaissement', key: 'de', width: 110,
            render: v => v ? formatDate(v) : '-'
        },
        {
            title: t('encaissements.colonnes.statut'), dataIndex: 'statutCheque', key: 'st', width: 130,
            render: v => (
                <Tag color={STATUT_COLORS[v] || 'default'} style={{ borderRadius: 20 }}>
                    {STATUT_LABELS[v] || v}
                </Tag>
            ),
        },
        {
            title: t('encaissements.colonnes.actions'), key: 'actions', width: 110, fixed: 'right',
            render: (_, row) => (
                <Space size="small">
                    {row.statutCheque === 'RECU' && (
                        <Tooltip title={t('encaissements.actions.marquerEncaisse')}>
                            <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                                onClick={() => setEncaisserModal({ open: true, item: row })}
                                style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }} />
                        </Tooltip>
                    )}
                    {row.statutCheque !== 'ANNULE' && (
                        <Tooltip title={t('encaissements.actions.annulerCheque')}>
                            <Button size="small" danger icon={<CloseCircleOutlined />}
                                onClick={() => setAnnulerModal({ open: true, item: row })} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    //  Handlers modal fermeture 
    const handleModalClose = () => {
        setModalOpen(false);
        form.resetFields();
        setTypeEmetteur(null);
        setOrganismeEmetteurId(null);
    };

    return (
        <div>
            {/* Section 1 : Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t('encaissements.titre')}</h3>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>{t('encaissements.sousTitre')}</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Tooltip title={!isDossierMurOuPlus(sinistre?.statut)
                        ? t('encaissements.dossierNonMur.infobulle')
                        : ''}>
                        <Button type="primary" icon={<PlusOutlined />}
                            disabled={!isDossierMurOuPlus(sinistre?.statut)}
                            onClick={() => setModalOpen(true)}
                            style={isDossierMurOuPlus(sinistre?.statut)
                                ? { background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }
                                : { borderRadius: 8 }}>
                            {t('encaissements.actions.nouveau')}
                        </Button>
                    </Tooltip>
                    <Tooltip title={t('encaissements.actions.actualiser')}>
                        <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }} />
                    </Tooltip>
                </div>
            </div>

            {!isDossierMurOuPlus(sinistre?.statut) && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    message={t('encaissements.dossierNonMur.titre')}
                    description={
                        <span>
                            <Trans t={t} i18nKey="encaissements.dossierNonMur.texte"
                                values={{ statut: STATUT_MAP[sinistre?.statut]?.label || sinistre?.statut || '-' }}
                                components={{ b: <strong /> }} />
                        </span>
                    }
                />
            )}

            {/* Banner préfinancements à rembourser */}
            {prefinsARembourser.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    message={t('encaissements.prefinancements.titre', { nombre: prefinsARembourser.length })}
                    description={
                        <span>
                            <Trans t={t} i18nKey="encaissements.prefinancements.texte"
                                values={{ montant: formatMontant(prefinsARembourser.reduce((a, p) => a + Number(p.resteARembourser || 0), 0)) }}
                                components={{ b: <strong /> }} />
                        </span>
                    }
                    action={
                        <Button size="small" onClick={() => {
                            window.dispatchEvent(new CustomEvent('cb:goto-tab', { detail: 'prefinancements' }));
                        }}>
                            {t('encaissements.prefinancements.voir')}
                        </Button>
                    }
                />
            )}

            {/* KPIs */}
            <KpiCards
                items={[
                    { label: t('encaissements.kpi.totalCheques'), value: filtered.length, color: '#1a6b3c', bg: '#f0fdf4', suffix: t('encaissements.kpi.cheques', { count: filtered.length }) },
                    { label: t('encaissements.kpi.montantCumule'), value: formatMontant(totaux.montant), color: '#059669', bg: '#ecfdf5' },
                    { label: t('encaissements.kpi.fraisGestion'), value: formatMontant(totaux.frais), color: '#0891b2', bg: '#ecfeff' },
                    { label: t('encaissements.kpi.enAttente'), value: enAttente, color: '#2563eb', bg: '#eff6ff', suffix: t('encaissements.kpi.chequesEnAttente', { count: enAttente }) },
                ]}
            />

            {/* Filtres */}
            <div className="cb-card" style={{ padding: 12, marginBottom: 16 }}>
                <Row gutter={12}>
                    <Col xs={24} md={16}>
                        <Input prefix={<SearchOutlined />}
                            placeholder={t('encaissements.filtres.recherche')}
                            value={search} onChange={e => setSearch(e.target.value)} allowClear />
                    </Col>
                    <Col xs={24} md={8}>
                        <Select placeholder={t('encaissements.filtres.tousStatuts')} style={{ width: '100%' }}
                            value={filtreStatut || undefined}
                            onChange={v => setFiltreStatut(v || '')} allowClear>
                            <Option value="RECU">{t('encaissements.statuts.RECU')}</Option>
                            <Option value="ENCAISSE">{t('encaissements.statuts.ENCAISSE')}</Option>
                            <Option value="REVENU_SANS_PROVISION">{t('encaissements.statuts.REVENU_SANS_PROVISION')}</Option>
                            <Option value="ANNULE">{t('encaissements.statuts.ANNULE')}</Option>
                        </Select>
                    </Col>
                </Row>
            </div>

            {/* Organisme émetteur en bandeau */}
            {organismeEmetteur && (
                <div style={{
                    marginBottom: 12,
                    padding: '8px 16px',
                    background: '#f0fdf4',
                    borderRadius: 4,
                    fontSize: 13,
                    color: '#1a6b3c',
                }}>
                    <strong>{t('encaissements.organismeEmetteur')}</strong> {organismeEmetteur}
                </div>
            )}

            {/* Tableau */}
            <div className="cb-card" style={{ padding: 0 }}>
                <Table className="cb-table" dataSource={filtered} columns={cols}
                    rowKey="encaissementTrackingId" loading={loading} scroll={{ x: 1200 }}
                    pagination={{ pageSize: 20, showTotal: n => t('encaissements.total', { count: n }) }}
                    locale={{
                        emptyText: (
                            <Empty
                                description={search || filtreStatut ? t('encaissements.aucunResultat') : t('encaissements.aucunEncaissement')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )
                    }} />
            </div>

            {/* Bouton export flottant */}
            <TableExportButton
                floating
                data={filtered}
                columns={[
                    { title: t('encaissements.colonnes.numeroCheque'), dataIndex: 'numeroCheque' },
                    { title: t('encaissements.export.organismeEmetteur'), dataIndex: 'organismeEmetteurRaisonSociale' },
                    { title: t('encaissements.export.montantCheque'), dataIndex: 'montantCheque', format: 'currency', numeric: true },
                    { title: t('encaissements.export.montantTheorique'), dataIndex: 'montantTheorique', format: 'currency', numeric: true },
                    { title: t('encaissements.colonnes.fraisGestion'), dataIndex: 'produitFraisGestion', format: 'currency', numeric: true },
                    { title: t('encaissements.colonnes.banque'), dataIndex: 'banqueEmettrice' },
                    { title: t('encaissements.export.dateReception'), dataIndex: 'dateReception', format: 'date' },
                    { title: t('encaissements.export.dateEncaissement'), dataIndex: 'dateEncaissement', format: 'date' },
                    { title: t('encaissements.colonnes.statut'), dataIndex: 'statutCheque', export: (v) => STATUT_LABELS[v] || v || '' },
                    { title: t('encaissements.export.motifAnnulation'), dataIndex: 'motifAnnulation' },
                ]}
                filename={`encaissements_sinistre_${sinistre?.numeroSinistreLocal || sinistreId?.slice(0, 8) || 'export'}`}
                title={t('encaissements.export.titre', { numero: sinistre?.numeroSinistreLocal || '' })}
                subtitle={sinistre?.organismeHomologueRaisonSociale ? t('encaissements.export.bureauHomologue', { bureau: sinistre.organismeHomologueRaisonSociale }) : undefined}
                count={filtered.length}
                logo={logoLocal}
            />

            {/*  Modal création  */}
            {/* 820 et non 620. Les valeurs saisies - numero de cheque, banque,
                montant - arrivaient tronquees dans leurs champs, et un montant
                coupe se relit mal au moment ou il faut le verifier. */}
            <Modal title={t('encaissements.creation.titre')} open={modalOpen}
                onCancel={handleModalClose}
                footer={null} width={820} destroyOnClose>
                {sinistre && (
                    <div style={{ background: '#f9fafb', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{t('encaissements.creation.sinistre')}</div>
                        <div style={{ fontFamily: 'var(--font-main)', fontWeight: 600, fontSize: 14 }}>
                            {sinistre.numeroSinistreLocal || sinistre.sinistreReference || '-'}
                        </div>
                    </div>
                )}
                <Form form={form} layout="vertical" onFinish={creer}
                    initialValues={{
                        dateReception: dayjs(),
                        organismeTrackingId: sinistre?.organismeHomologueTrackingId,
                    }}>
                    <Row gutter={16}>

                        {/* Bureau homologue verrouillé */}
                        <Col span={12}>
                            <Form.Item name="organismeTrackingId" label={t('encaissements.creation.bureauHomologue')}
                                tooltip={t('encaissements.creation.bureauHomologueAide')}
                                rules={[{ required: true, message: t('encaissements.creation.bureauHomologueRequis') }]}>
                                <Select disabled
                                    options={sinistre?.organismeHomologueTrackingId ? [{
                                        value: sinistre.organismeHomologueTrackingId,
                                        label: sinistre.organismeHomologueRaisonSociale || sinistre.organismeHomologueTrackingId,
                                    }] : []} />
                            </Form.Item>
                        </Col>

                        {/* N° chèque */}
                        <Col span={12}>
                            <Form.Item name="numeroCheque" label={t('encaissements.creation.numeroCheque')}
                                rules={[{ required: true, message: t('encaissements.creation.numeroRequis') }]}>
                                <Input placeholder={t('encaissements.creation.numeroExemple')} />
                            </Form.Item>
                        </Col>

                        {/* Type d'émetteur */}
                        <Col span={12}>
                            <Form.Item
                                label={t('encaissements.creation.typeEmetteur')}
                                required
                                validateStatus={!typeEmetteur ? undefined : undefined}
                                tooltip={t('encaissements.creation.typeEmetteurAide')}>
                                <Select
                                    placeholder={t('encaissements.creation.typeEmetteurPlaceholder')}
                                    value={typeEmetteur}
                                    onChange={val => {
                                        setTypeEmetteur(val || null);
                                        setOrganismeEmetteurId(null);
                                    }}
                                    allowClear
                                    options={TYPES_EMETTEUR}
                                />
                            </Form.Item>
                        </Col>

                        {/* Organisme émetteur (filtré par type) */}
                        <Col span={12}>
                            <Form.Item
                                label={t('encaissements.creation.organismeEmetteur')}
                                required
                                tooltip={t('encaissements.creation.organismeEmetteurAide')}>
                                <Select
                                    placeholder={isFetchingOrganismes ? t('encaissements.creation.chargement') : t('encaissements.creation.organismeEmetteurPlaceholder')}
                                    value={organismeEmetteurId}
                                    onChange={val => setOrganismeEmetteurId(val || null)}
                                    disabled={!typeEmetteur}
                                    loading={isFetchingOrganismes}
                                    allowClear
                                    showSearch
                                    filterOption={(input, opt) =>
                                        (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={organismes.map(o => ({
                                        value: o.organismeTrackingId,
                                        label: o.raisonSociale,
                                    }))}
                                />
                            </Form.Item>
                        </Col>

                        {/* Chèque à l'ordre de (BNCB pré-sélectionné) */}
                        <Col span={12}>
                            <Form.Item
                                label={t('encaissements.creation.chequeOrdre')}
                                tooltip={t('encaissements.creation.chequeOrdreAide')}>
                                <Select
                                    value={chequeOrdreOrganismeId}
                                    onChange={val => setChequeOrdreOrganismeId(val)}
                                    options={bncbList.map(o => ({
                                        value: o.organismeTrackingId,
                                        label: o.raisonSociale,
                                    }))}
                                />
                            </Form.Item>
                        </Col>

                        {/* Banque émettrice */}
                        <Col span={12}>
                            <Form.Item name="banqueEmettrice" label={t('encaissements.creation.banqueEmettrice')}>
                                <Select showSearch allowClear placeholder={t('encaissements.creation.banquePlaceholder')}
                                    filterOption={(i, o) => (o.label || '').toLowerCase().includes(i.toLowerCase())}
                                    options={banques.map(b => ({
                                        value: b.nom,
                                        label: b.code ? `${b.nom} (${b.code})` : b.nom,
                                    }))} />
                            </Form.Item>
                        </Col>

                        {/* Montant chèque */}
                        <Col span={12}>
                            <Form.Item name="montantCheque" label={t('encaissements.creation.montantCheque')}
                                rules={[{ required: true, message: t('encaissements.creation.requis') }]}>
                                <CBInputNumber {...moneyProps} />
                            </Form.Item>
                        </Col>

                        {/* Montant théorique */}
                        <Col span={12}>
                            <Form.Item name="montantTheorique" label={t('encaissements.creation.montantTheorique')}
                                rules={[{ required: true, message: t('encaissements.creation.requis') }]}>
                                <CBInputNumber {...moneyProps} />
                            </Form.Item>
                        </Col>

                        {/* Frais de gestion - la case décide, le montant se calcule.

                            Ils portent sur le montant THÉORIQUE et non sur le chèque : le
                            chèque est ce qui a été reçu, le théorique ce qui était dû. Les
                            asseoir sur le chèque faisait varier la commission du bureau avec
                            une erreur de l'émetteur - un trop-perçu l'augmentait, un versement
                            partiel la diminuait, sans qu'aucune décision de gestion l'ait voulu.

                            La case remplace une règle qui ne souffrait aucune exception. Un
                            remboursement, une régularisation ou un accord particulier peuvent
                            n'avoir pas à les supporter, et il fallait corriger l'écriture après
                            coup. Pré-cochée selon l'ancienne règle : le cas courant ne change
                            pas de geste. */}
                        <Col span={12}>
                            <Form.Item
                                name="appliquerFraisGestion"
                                valuePropName="checked"
                                initialValue={sinistre?.typeSinistre === 'SURVENU_TOGO'}
                                style={{ marginBottom: 4 }}>
                                <Checkbox>{t('encaissements.creation.appliquerFrais')}</Checkbox>
                            </Form.Item>
                            <Form.Item
                                dependencies={['montantTheorique', 'montantCheque', 'appliquerFraisGestion']}
                                noStyle>
                                {({ getFieldValue }) => {
                                    if (!getFieldValue('appliquerFraisGestion')) return null;
                                    // Repli sur le chèque quand le théorique n'est pas encore
                                    // saisi : même règle que le serveur, pour que l'aperçu ne
                                    // promette jamais autre chose que ce qui sera enregistré.
                                    const theorique = Number(getFieldValue('montantTheorique') || 0);
                                    const cheque = Number(getFieldValue('montantCheque') || 0);
                                    const assiette = theorique > 0 ? theorique : cheque;
                                    const frais = Math.round(assiette * 0.05 * 100) / 100;
                                    return (
                                        <Form.Item
                                            label={t('encaissements.creation.fraisGestion')}
                                            tooltip={t('encaissements.creation.fraisGestionAide')}>
                                            <CBInputNumber {...moneyProps} disabled value={frais} />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </Col>

                        {/* Date d'émission */}
                        <Col span={12}>
                            <Form.Item name="dateEmission" label={t('encaissements.creation.dateEmission')}
                                rules={[{ required: true, message: t('encaissements.creation.dateRequise') }]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>

                        {/* Date de réception */}
                        <Col span={12}>
                            <Form.Item name="dateReception" label={t('encaissements.creation.dateReception')}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>

                    </Row>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                        <Button onClick={handleModalClose}>{t('encaissements.creation.annuler')}</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={creerEncaissement.isPending}
                            disabled={!organismeEmetteurId || !chequeOrdreOrganismeId}
                            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
                            {t('encaissements.creation.enregistrer')}
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/*  Modal encaisser  */}
            <Modal title={t('encaissements.encaisser.titre')} open={encaisserModal.open}
                onCancel={() => { setEncaisserModal({ open: false, item: null }); encaisserForm.resetFields(); }}
                footer={null} width={460} destroyOnClose>
                {encaisserModal.item && (
                    <div style={{ background: '#f9fafb', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{t('encaissements.encaisser.cheque')}</div>
                        <div style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}>{encaisserModal.item.numeroCheque}</div>
                        <div style={{ marginTop: 4, fontWeight: 600 }}>{formatMontant(encaisserModal.item.montantCheque)}</div>
                    </div>
                )}
                <Form form={encaisserForm} layout="vertical" onFinish={encaisser}
                    initialValues={{ dateEncaissement: dayjs() }}>
                    <Form.Item name="dateEncaissement" label={t('encaissements.encaisser.date')}
                        rules={[{ required: true, message: t('encaissements.creation.dateRequise') }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={() => setEncaisserModal({ open: false, item: null })}>{t('encaissements.encaisser.annuler')}</Button>
                        <Button type="primary" htmlType="submit"
                            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
                            {t('encaissements.encaisser.confirmer')}
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/*  Modal annuler  */}
            <Modal title={t('encaissements.annuler.titre')} open={annulerModal.open}
                onCancel={() => { setAnnulerModal({ open: false, item: null }); annulerForm.resetFields(); }}
                footer={null} width={460} destroyOnClose>
                {annulerModal.item && (
                    <div style={{ background: '#fef2f2', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #fecaca' }}>
                        <div style={{ fontSize: 13, color: '#991b1b' }}>{t('encaissements.annuler.cheque')}</div>
                        <div style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}>{t('encaissements.annuler.chequeBanque', { numero: annulerModal.item.numeroCheque, banque: sinistre?.banqueEmettrice })}</div>
                    </div>
                )}
                <Form form={annulerForm} layout="vertical" onFinish={annuler}>
                    <Form.Item name="motif" label={t('encaissements.annuler.motif')}
                        rules={[{ required: true, message: t('encaissements.annuler.motifRequis') }]}>
                        <Input.TextArea rows={3} placeholder={t('encaissements.annuler.motifExemple')} />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={() => setAnnulerModal({ open: false, item: null })}>{t('encaissements.annuler.retour')}</Button>
                        <Button danger type="primary" htmlType="submit">{t('encaissements.annuler.confirmer')}</Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
}